import { ClientRect, Modifier, UniqueIdentifier } from "@dnd-kit/core";
import { IItemType } from "./SortableListQuiz";
import { Transform } from "@dnd-kit/utilities";

export const countInputsInTemplate = (template: string): number => {
  const matches = template?.match(/{{INPUT(?::[^}]*?)?}}/g);
  return matches ? matches.length : 0;
};

export const getCorrectList = (
  items: IItemType[],
  orderListkeys: (string | number)[]
) => {
  return orderListkeys.map((key) => items.find((item) => item.key === key)!);
};

export const reorderItems = (
  items: IItemType[],
  key1: UniqueIdentifier | null,
  key2: UniqueIdentifier | null
) => {
  const updatedList = [...items];

  const index1 = updatedList.findIndex((item) => item.key === key1);
  const index2 = updatedList.findIndex((item) => item.key === key2);

  if (index1 === -1 || index2 === -1) {
    console.error("One or both keys not found");
    return items;
  }

  [updatedList[index1], updatedList[index2]] = [
    updatedList[index2],
    updatedList[index1],
  ];

  return updatedList;
};

export const restrictToVerticalAxis: Modifier = ({ transform }) => {
  return {
    ...transform,
    x: 0,
  };
};

export const restrictToWindowEdges: Modifier = ({
  transform,
  draggingNodeRect,
  windowRect,
}) => {
  if (!draggingNodeRect || !windowRect) {
    return transform;
  }

  return restrictToBoundingRect(transform, draggingNodeRect, windowRect);
};

export function restrictToBoundingRect(
  transform: Transform,
  rect: ClientRect,
  boundingRect: ClientRect
): Transform {
  const value = {
    ...transform,
  };

  if (rect.top + transform.y <= boundingRect.top) {
    value.y = boundingRect.top - rect.top;
  } else if (
    rect.bottom + transform.y >=
    boundingRect.top + boundingRect.height
  ) {
    value.y = boundingRect.top + boundingRect.height - rect.bottom;
  }

  if (rect.left + transform.x <= boundingRect.left) {
    value.x = boundingRect.left - rect.left;
  } else if (
    rect.right + transform.x >=
    boundingRect.left + boundingRect.width
  ) {
    value.x = boundingRect.left + boundingRect.width - rect.right;
  }

  return value;
}

export const adjustTranslate: Modifier = ({ transform }) => {
  return {
    x: transform.x + 50,
    y: transform.y,
    scaleX: 0,
    scaleY: 0,
  };
};

export const compareHtml = (html1: string, html2: string) => {
  const parser = new DOMParser();

  const dom1 = parser.parseFromString(html1, "text/html");
  const dom2 = parser.parseFromString(html2, "text/html");

  function compareNodes(node1: any, node2: any) {
    // Check tag name
    if (node1.nodeName !== node2.nodeName) return false;

    // Compare attributes
    const attrs1 = node1.attributes ? [...node1.attributes] : [];
    const attrs2 = node2.attributes ? [...node2.attributes] : [];
    if (attrs1.length !== attrs2.length) return false;

    const attrMap1 = new Map(attrs1.map((attr) => [attr.name, attr.value]));
    const attrMap2 = new Map(attrs2.map((attr) => [attr.name, attr.value]));
    if (
      JSON.stringify([...attrMap1.entries()]) !==
      JSON.stringify([...attrMap2.entries()])
    ) {
      return false;
    }

    // Compare child nodes
    const children1 = [...node1.childNodes].filter(
      (node) => node.nodeType !== 8
    ); // Ignore comments
    const children2 = [...node2.childNodes].filter(
      (node) => node.nodeType !== 8
    );

    if (children1.length !== children2.length) return false;

    for (let i = 0; i < children1.length; i++) {
      if (!compareNodes(children1[i], children2[i])) return false;
    }

    return true;
  }

  return compareNodes(dom1.documentElement, dom2.documentElement);
};

export function swapKeyValue<T extends string | number | symbol>(
  input: Record<string, T | null>
): Record<T, string> {
  const result: any = {};

  Object.entries(input).forEach(([key, value]) => {
    if (value !== null) {
      result[value] = key;
    }
  });

  return result;
}
